import { c as _c } from "react/compiler-runtime";
import { estimateShopCustomOrderPrice } from "@osmix/contracts/shop-custom-order";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";
import { api, type ShopCustomOrder, type ShopCustomOrderPaymentType, type ShopCustomOrderSettings } from "@/api";
import { MinusIcon, PlusIcon, XIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import { Segmented } from "@/components/ui/segmented";
import { Input } from "@/components/ui/text-field";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useProfile } from "@/store/profile";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "@osmix/ui/dialog";

// fd-boutique-13: мастер индивидуального заказа с витрины магазина — шаги по
// ТЗ: тип изделия → описание → размеры и параметры → фото-референсы → бюджет →
// срок → способ оплаты → контакты (с проверкой заявки). Если продавец задал
// прайс, внизу окна — предварительная цена (та же формула, что на шлюзе).
// Заявка уходит продавцу тредом в чатах, дальше — смета и заказ из сметы.
//
// Вид — по кадру «Индивидуальный заказ» конструктора бутика: окно 553, плитка
// шага «1/8» 66×66 r16 на #F8F8F8, подсказка 16/22 серым, поле, «Далее».

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shop: {
    slug: string;
    name: string;
  };
  settings?: ShopCustomOrderSettings;
  onSubmitted?: (order: ShopCustomOrder) => void;
};
const STEPS = ["type", "description", "size", "photos", "budget", "deadline", "payment", "contacts"] as const;
type Step = (typeof STEPS)[number];
const OTHER = "__other";
const MAX_PHOTOS = 6;
const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const ruDate = (ymd: string) => ymd.split("-").reverse().join(".");
const rub = (n: number) => `${new Intl.NumberFormat("ru-RU").format(n)} руб.`;
export function ManufacturingWizard({
  open,
  onOpenChange,
  shop,
  settings,
  onSubmitted
}: Props) {
  const {
    t
  } = useTranslation();
  const user = useProfile(s => s.user);
  const signedIn = Boolean(useProfile(s_0 => s_0.token));
  const priceTypes = settings?.productTypes ?? [];
  const payments: ShopCustomOrderPaymentType[] = settings?.paymentTypes?.length ? settings.paymentTypes : ["card", "invoice"];
  const [stepIdx, setStepIdx] = useState(0);
  const [typeId, setTypeId] = useState<string>("");
  const [customType, setCustomType] = useState("");
  const [description, setDescription] = useState("");
  const [size, setSize] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [parameters, setParameters] = useState("");
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [budget, setBudget] = useState("");
  const [deadline, setDeadline] = useState("");
  const [payment, setPayment] = useState<ShopCustomOrderPaymentType>(payments[0] ?? "card");
  const [contactName, setContactName] = useState(user?.name ?? "");
  const [contactMethod, setContactMethod] = useState<"chat" | "phone">("chat");
  const [contactPhone, setContactPhone] = useState(user?.phone ?? "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<ShopCustomOrder | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const step: Step = STEPS[stepIdx] ?? "type";
  const priceItem = priceTypes.find(p => p.id === typeId);
  const productType = typeId === OTHER ? customType.trim() : priceItem?.name ?? "";
  const estimate = estimateShopCustomOrderPrice(settings, {
    productTypeId: priceItem?.id,
    quantity,
    deadline: deadline || undefined
  });
  const urgent = Boolean(settings?.urgentMarkupPercent && settings.leadDays && deadline) && (Date.parse(`${deadline}T00:00:00`) - Date.parse(`${todayYmd()}T00:00:00`)) / 86_400_000 < (settings?.leadDays ?? 0);
  const reset = () => {
    setStepIdx(0);
    setTypeId("");
    setCustomType("");
    setDescription("");
    setSize("");
    setQuantity(1);
    setParameters("");
    setPhotos([]);
    setBudget("");
    setDeadline("");
    setPayment(payments[0] ?? "card");
    setContactMethod("chat");
    setDone(null);
  };
  const close = (o: boolean) => {
    onOpenChange(o);
    if (!o) reset();
  };
  const canNext = (() => {
    switch (step) {
      case "type":
        return productType.length > 0;
      case "description":
        return description.trim().length >= 2;
      case "size":
        return quantity >= 1;
      case "budget":
        return budget === "" || /^\d+$/.test(budget.replace(/\s/g, ""));
      case "deadline":
        return deadline === "" || deadline >= todayYmd();
      case "contacts":
        return contactName.trim().length > 0 && (contactMethod === "chat" || contactPhone.trim().length >= 5);
      default:
        return true;
    }
  })();
  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    const room = MAX_PHOTOS - photos.length;
    const picked = Array.from(files).filter(f => f.type.startsWith("image/")).slice(0, room);
    if (picked.length === 0) return;
    setUploading(true);
    try {
      const urls: string[] = [];
      for (const file of picked) urls.push((await api.buyer.uploadChatImage(file)).url);
      setPhotos(prev => [...prev, ...urls].slice(0, MAX_PHOTOS));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const submit = async () => {
    if (!canNext) return;
    setBusy(true);
    try {
      const order = await api.shops.createCustomOrder(shop.slug, {
        productTypeId: priceItem?.id,
        productType,
        description: description.trim(),
        quantity,
        size: size.trim() || undefined,
        parameters: parameters.trim() || undefined,
        photos,
        budget: budget ? Number(budget.replace(/\s/g, "")) : undefined,
        deadline: deadline || undefined,
        paymentType: payment,
        contactName: contactName.trim(),
        contactPhone: contactMethod === "phone" ? contactPhone.trim() : undefined,
        contactMethod
      });
      setDone(order);
      onSubmitted?.(order);
    } catch (e_0) {
      toast.error(e_0 instanceof Error ? e_0.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };
  const label = "text-[14px] leading-[17px] font-semibold text-ds-primary";
  return <Dialog open={open} onOpenChange={close}>
			<DialogContent showCloseButton={false} overlayClassName="bg-black/20 backdrop-blur-[1.5px]" className="flex max-h-[92dvh] w-[95vw] max-w-[553px] flex-col gap-6 overflow-y-auto rounded-[20px] border-0 bg-white p-6 shadow-none sm:max-w-[553px]" data-testid="custom-order-wizard">
				<div className="flex items-start justify-between gap-4">
					<DialogTitle className="text-[20px] leading-5 font-bold text-ds-primary">{t("shop.customOrder.title")}</DialogTitle>
					<DialogClose aria-label={t("common.close")} className="flex size-6 shrink-0 items-center justify-center rounded-[4px] text-ds-ink-3 outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">
						<XIcon />
					</DialogClose>
				</div>
				<DialogDescription className="sr-only">{t("shop.customOrder.forShop", {
          shop: shop.name
        })}</DialogDescription>

				{!signedIn ? <div className="flex flex-col items-start gap-3" data-testid="wizard-sign-in">
						<p className="text-[16px] leading-[22px] font-semibold text-ds-primary">{t("requests.signInTitle")}</p>
						<p className="text-[14px] leading-5 text-ds-secondary">{t("shop.customOrder.signInHint")}</p>
						<Button asChild size="md">
							<Link to="/profile" onClick={() => close(false)}>
								{t("requests.goToSignIn")}
							</Link>
						</Button>
					</div> : done ? <div className="flex flex-col items-start gap-4" data-testid="custom-order-done">
						<p className="text-[16px] leading-[22px] font-semibold text-ds-primary">{t("shop.customOrder.sentTitle")}</p>
						<p className="text-[14px] leading-5 text-ds-secondary">{t("shop.customOrder.sentBody", {
            shop: shop.name
          })}</p>
						<div className="flex flex-wrap gap-2.5">
							<Button asChild size="md">
								<Link to={`/chats?conversation=${encodeURIComponent(done.conversationId)}`} onClick={() => close(false)}>
									{t("shop.customOrder.openChat")}
								</Link>
							</Button>
							<Button variant="gray" size="md" onClick={() => close(false)}>
								{t("shop.customOrder.done")}
							</Button>
						</div>
					</div> : <>
						{/* Шаг: плитка «N/8» и подсказка. */}
						<div className="flex items-center gap-4">
							<div className="flex size-[66px] shrink-0 items-center justify-center rounded-[16px] bg-ds-quaternary text-[24px] leading-[34px] font-medium text-ds-primary">
								{stepIdx + 1}/{STEPS.length}
							</div>
							<div className="flex min-w-0 flex-col gap-1">
								<p className="text-[16px] leading-[22px] font-semibold text-ds-primary">{t(`shop.customOrder.steps.${step}`)}</p>
								<p className="text-[14px] leading-5 text-ds-secondary">{t(`shop.customOrder.hints.${step}`)}</p>
							</div>
						</div>
						<div className="flex gap-1" aria-hidden>
							{STEPS.map((s_1, i) => <span key={s_1} className={cn("h-1 flex-1 rounded-full", i <= stepIdx ? "bg-ds-accent" : "bg-ds-gray")} />)}
						</div>

						<div className="flex min-h-[160px] flex-col gap-4">
							{step === "type" && <>
									<div className="flex flex-wrap gap-2.5">
										{priceTypes.map(p_0 => <Chip key={p_0.id} size="lg" selected={typeId === p_0.id} onClick={() => setTypeId(p_0.id)}>
												{p_0.name}
												{p_0.price ? <span className="ml-1.5 text-ds-secondary">{t("shop.customOrder.priceFrom", {
                    price: rub(p_0.price)
                  })}</span> : null}
											</Chip>)}
										<Chip size="lg" selected={typeId === OTHER} onClick={() => setTypeId(OTHER)}>
											{priceTypes.length ? t("shop.customOrder.otherType") : t("shop.customOrder.ownType")}
										</Chip>
									</div>
									{typeId === OTHER && <div className="flex flex-col gap-2">
											<label htmlFor="co-type" className={label}>
												{t("shop.customOrder.typeLabel")}
											</label>
											<Input id="co-type" autoFocus value={customType} maxLength={80} onChange={e_1 => setCustomType(e_1.target.value)} placeholder={t("shop.customOrder.typePlaceholder")} />
										</div>}
								</>}

							{step === "description" && <div className="flex flex-col gap-2">
									<label htmlFor="co-desc" className={label}>
										{t("shop.customOrder.descriptionLabel")}
									</label>
									<Textarea id="co-desc" autoFocus rows={5} maxLength={2000} value={description} onChange={e_2 => setDescription(e_2.target.value)} placeholder={t("shop.customOrder.descriptionPlaceholder")} />
									<span className="self-end text-[12px] text-ds-muted">{description.length} / 2000</span>
								</div>}

							{step === "size" && <>
									<div className="grid gap-4 sm:grid-cols-[1fr_auto]">
										<div className="flex flex-col gap-2">
											<label htmlFor="co-size" className={label}>
												{t("shop.customOrder.sizeLabel")}
											</label>
											<Input id="co-size" value={size} maxLength={120} onChange={e_3 => setSize(e_3.target.value)} placeholder={t("shop.customOrder.sizePlaceholder")} />
										</div>
										<div className="flex flex-col gap-2">
											<span className={label}>{t("shop.customOrder.quantityLabel")}</span>
											<div className="flex h-10 items-center gap-1 rounded-[20px] bg-ds-field px-1">
												<button type="button" aria-label={t("shop.customOrder.less")} disabled={quantity <= 1} onClick={() => setQuantity(q => Math.max(1, q - 1))} className="grid size-8 place-items-center rounded-full outline-none hover:bg-black/5 focus-visible:ring-2 focus-visible:ring-ds-focus disabled:opacity-40">
													<MinusIcon size={16} />
												</button>
												<span className="min-w-8 text-center text-[14px] font-semibold" data-testid="co-qty">
													{quantity}
												</span>
												<button type="button" aria-label={t("shop.customOrder.more")} disabled={quantity >= 1000} onClick={() => setQuantity(q_0 => Math.min(1000, q_0 + 1))} className="grid size-8 place-items-center rounded-full outline-none hover:bg-black/5 focus-visible:ring-2 focus-visible:ring-ds-focus disabled:opacity-40">
													<PlusIcon size={16} />
												</button>
											</div>
										</div>
									</div>
									<div className="flex flex-col gap-2">
										<label htmlFor="co-params" className={label}>
											{t("shop.customOrder.parametersLabel")}
										</label>
										<Textarea id="co-params" rows={3} maxLength={1000} value={parameters} onChange={e_4 => setParameters(e_4.target.value)} placeholder={t("shop.customOrder.parametersPlaceholder")} />
									</div>
								</>}

							{step === "photos" && <div className="flex flex-col gap-3">
									<div className="flex flex-wrap gap-2.5">
										{photos.map(src => <div key={src} className="relative h-[107px] w-20 overflow-hidden rounded-[8px] bg-ds-quaternary">
												<img src={src} alt="" className="size-full object-cover" />
												<button type="button" aria-label={t("shop.customOrder.removePhoto")} onClick={() => setPhotos(prev_0 => prev_0.filter(p_1 => p_1 !== src))} className="absolute top-1 right-1 grid size-6 place-items-center rounded-full bg-white/90 text-ds-primary outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">
													<XIcon size={14} />
												</button>
											</div>)}
										{photos.length < MAX_PHOTOS && <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} className="flex h-[107px] w-20 flex-col items-center justify-center gap-1 rounded-[8px] border border-dashed border-ds-line-2 text-[12px] text-ds-secondary outline-none hover:border-ds-accent focus-visible:ring-2 focus-visible:ring-ds-focus disabled:opacity-50">
												<PlusIcon size={20} />
												{uploading ? t("shop.customOrder.uploading") : t("shop.customOrder.addPhoto")}
											</button>}
									</div>
									<input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={e_5 => void onFiles(e_5.target.files)} data-testid="co-photo-input" />
									<p className="text-[12px] leading-4 text-ds-muted">{t("shop.customOrder.photosNote", {
                count: MAX_PHOTOS
              })}</p>
								</div>}

							{step === "budget" && <div className="flex flex-col gap-2">
									<label htmlFor="co-budget" className={label}>
										{t("shop.customOrder.budgetLabel")}
									</label>
									<Input id="co-budget" autoFocus inputMode="numeric" value={budget} onChange={e_6 => setBudget(e_6.target.value.replace(/[^\d\s]/g, ""))} placeholder={t("shop.customOrder.budgetPlaceholder")} />
								</div>}

							{step === "deadline" && <div className="flex flex-col gap-2">
									<label htmlFor="co-deadline" className={label}>
										{t("shop.customOrder.deadlineLabel")}
									</label>
									<Input id="co-deadline" type="date" min={todayYmd()} value={deadline} onChange={e_7 => setDeadline(e_7.target.value)} />
									{settings?.leadDays ? <p className="text-[12px] leading-4 text-ds-secondary">
											{t("shop.customOrder.leadDays", {
                count: settings.leadDays
              })}
											{settings.urgentMarkupPercent ? ` ${t("shop.customOrder.urgentNote", {
                percent: settings.urgentMarkupPercent
              })}` : ""}
										</p> : null}
									{urgent && <p className="text-[12px] leading-4 text-ds-accent">{t("shop.customOrder.urgent")}</p>}
								</div>}

							{step === "payment" && <div className="flex flex-col gap-2.5" role="radiogroup" aria-label={t("shop.customOrder.steps.payment")}>
									{payments.map(p_2 => <button key={p_2} type="button" role="radio" aria-checked={payment === p_2} onClick={() => setPayment(p_2)} className={cn("flex flex-col items-start gap-1 rounded-[16px] border p-4 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ds-focus", payment === p_2 ? "border-ds-primary bg-ds-quaternary" : "border-ds-line hover:border-ds-line-2")}>
											<span className="text-[14px] leading-[18px] font-semibold text-ds-primary">{t(`shop.customOrder.payment.${p_2}`)}</span>
											<span className="text-[12px] leading-4 text-ds-secondary">{t(`shop.customOrder.payment.${p_2}Hint`)}</span>
										</button>)}
								</div>}

							{step === "contacts" && <>
									<div className="flex flex-col gap-2">
										<label htmlFor="co-name" className={label}>
											{t("shop.customOrder.nameLabel")}
										</label>
										<Input id="co-name" value={contactName} maxLength={120} onChange={e_8 => setContactName(e_8.target.value)} />
									</div>
									<Segmented block aria-label={t("shop.customOrder.contactMethod")} value={contactMethod} onValueChange={setContactMethod} options={[{
              value: "chat",
              label: t("shop.customOrder.viaChat")
            }, {
              value: "phone",
              label: t("shop.customOrder.viaPhone")
            }]} />
									{contactMethod === "phone" && <div className="flex flex-col gap-2">
											<label htmlFor="co-phone" className={label}>
												{t("shop.customOrder.phoneLabel")}
											</label>
											<Input id="co-phone" type="tel" inputMode="tel" value={contactPhone} maxLength={40} onChange={e_9 => setContactPhone(e_9.target.value)} />
											<p className="text-[12px] leading-4 text-ds-secondary">{t("shop.customOrder.phoneHint")}</p>
										</div>}
									{/* Проверка заявки перед отправкой. */}
									<dl className="grid gap-1.5 rounded-[16px] bg-ds-quaternary p-4 text-[13px] leading-[18px]" data-testid="co-summary">
										<Row k={t("shop.customOrder.summary.type")} v={productType} />
										<Row k={t("shop.customOrder.summary.quantity")} v={`${quantity}`} />
										{size.trim() && <Row k={t("shop.customOrder.summary.size")} v={size.trim()} />}
										{photos.length > 0 && <Row k={t("shop.customOrder.summary.photos")} v={`${photos.length}`} />}
										{budget && <Row k={t("shop.customOrder.summary.budget")} v={rub(Number(budget.replace(/\s/g, "")))} />}
										{deadline && <Row k={t("shop.customOrder.summary.deadline")} v={ruDate(deadline)} />}
										<Row k={t("shop.customOrder.summary.payment")} v={t(`shop.customOrder.payment.${payment}`)} />
									</dl>
								</>}
						</div>

						{estimate != null && <div className="flex flex-col gap-0.5 rounded-[12px] bg-ds-accent/[0.06] px-4 py-3" data-testid="co-estimate">
								<span className="text-[14px] leading-[18px] font-semibold text-ds-primary">
									{t("shop.customOrder.estimate", {
              price: rub(estimate)
            })}
								</span>
								<span className="text-[12px] leading-4 text-ds-secondary">{t("shop.customOrder.estimateNote")}</span>
							</div>}

						<div className="flex items-center justify-between gap-2">
							<Button variant="gray" size="md" onClick={() => stepIdx === 0 ? close(false) : setStepIdx(i_0 => i_0 - 1)} disabled={busy}>
								{stepIdx === 0 ? t("common.cancel") : t("shop.customOrder.back")}
							</Button>
							{stepIdx < STEPS.length - 1 ? <Button size="md" onClick={() => setStepIdx(i_1 => i_1 + 1)} disabled={!canNext || uploading}>
									{t("shop.customOrder.next")}
								</Button> : <Button size="md" onClick={() => void submit()} disabled={busy || !canNext}>
									{busy ? t("shop.customOrder.submitting") : t("shop.customOrder.submit")}
								</Button>}
						</div>
					</>}
			</DialogContent>
		</Dialog>;
}
function Row(t0) {
  const $ = _c(7);
  const {
    k,
    v
  } = t0;
  let t1;
  if ($[0] !== k) {
    t1 = <dt className="text-ds-secondary">{k}</dt>;
    $[0] = k;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== v) {
    t2 = <dd className="text-right font-medium text-ds-primary">{v}</dd>;
    $[2] = v;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex justify-between gap-3">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}